import { useCallback } from 'react';
import styled from 'styled-components';

import Card from 'components/insights/layout/Card';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useReferralsInsightsActions, useReferralsInsightsStore } from 'stores/referralsInsightsStore';

import { getEntityColumnHeader, VIEW_BY_DIMENSION_MAP } from '../constants';
import { useReferralsInsightsContext } from '../ReferralsContainer';

import { EntityButton } from './referralDetailsColumns';
import ReferralDetailsHeader from './ReferralDetailsHeader';
import ReferralDetailsTable from './ReferralDetailsTable';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

export default function ReferralDetailsSection({ loading, rollups }: Props) {
  const { sendingGroupType } = useReferralsInsightsContext();
  const viewBy = useReferralsInsightsStore((state) => state.viewBy);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const { openSlideout } = useReferralsInsightsActions();

  const dimension = VIEW_BY_DIMENSION_MAP[viewBy];
  const sendingGroupTypeName = sendingGroupType?.displayName ?? '';
  const receivingGroupTypeName = selectedReceivingGroupType?.displayName ?? '';
  const entityHeader = getEntityColumnHeader(dimension, { sendingGroupTypeName, receivingGroupTypeName });

  const entityCell = useCallback(
    (info: { getValue: () => string; row: { original: { entityId: string; entityName: string } } }) => {
      const row = info.row.original;
      if (!row.entityId) return <span>{info.getValue()}</span>;
      return (
        <EntityButton
          type='button'
          onClick={() =>
            openSlideout({
              entityId: row.entityId,
              entityName: row.entityName,
              dimension,
            })
          }>
          {info.getValue()}
        </EntityButton>
      );
    },
    [dimension, openSlideout]
  );

  return (
    <SectionCard>
      <ReferralDetailsHeader />
      <TableWrapper>
        <ReferralDetailsTable
          loading={loading}
          rollups={rollups}
          dimension={dimension}
          entityHeader={entityHeader}
          entityCell={entityCell}
        />
      </TableWrapper>
    </SectionCard>
  );
}

const SectionCard = styled(Card)`
  padding: 0;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
`;

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 24px 24px;

  td:first-child:not(:last-child),
  th:first-child {
    border-right: 1px solid ${({ theme }) => theme.colors.black10};
  }

  thead {
    background-color: ${({ theme }) => theme.colors.white};
  }

  thead tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  }

  tbody {
    border-top: none;
  }
`;
